import { useLocation, useNavigate } from '@faceup/router'
import {
  convertLanguageToNativeLanguage,
  DEFAULT_LANGUAGE,
  Language,
  languageEnumToBasename,
  storageSet,
} from '@faceup/utils'
import { useContext, useMemo } from 'react'
import { LanguageContext } from '../App/LanguageContext'
import { StorageKeys } from '../utils/useStorage'

export type UseManageLanguageDropdownProps = {
  availableLanguages?: Language[] | null
  defaultLanguage?: Language
}
export const useManageLanguageDropdown = ({
  availableLanguages,
  defaultLanguage,
}: UseManageLanguageDropdownProps) => {
  const { language } = useContext(LanguageContext)
  const navigate = useNavigate()
  const location = useLocation()

  const alphabeticallySortedLanguages = useMemo(() => {
    const languages = availableLanguages ?? Object.values(Language)
    // available languages can be sealed object, therefore make a copy and then sort
    return Array.from(languages).sort((a, b) =>
      convertLanguageToNativeLanguage(a).localeCompare(convertLanguageToNativeLanguage(b))
    )
  }, [availableLanguages])

  const defaultSelectedLanguage = useMemo(() => {
    const isUserLanguageAvailable = language && alphabeticallySortedLanguages.includes(language)

    return isUserLanguageAvailable ? language : (defaultLanguage ?? DEFAULT_LANGUAGE)
  }, [alphabeticallySortedLanguages, defaultLanguage, language])

  // Language lives in the URL :language segment. Switching means navigating to the
  // same path with that segment swapped; the route wrapper narrows it and the
  // LanguageProvider swaps messages in place.
  const changeLanguage = (newLanguage: Language) => {
    const basename = languageEnumToBasename(newLanguage)
    // Persist the explicit choice so prefix-less entry URLs (bare root, shared
    // links, QR codes) resolve to it via getInitialLanguage instead of falling
    // back to the browser language.
    storageSet('localStorage', StorageKeys.Language, basename)
    const segments = location.pathname.split('/')
    segments[1] = basename
    const target = `${segments.join('/')}${location.search}`
    navigate(() => target)
  }

  return {
    language,
    alphabeticallySortedLanguages,
    defaultSelectedLanguage,
    changeLanguage,
  }
}
